import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DictionaryField,
  DocSignService,
  EgibObject,
  MapAction,
  MapExtentUtils,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  PortalId,
  ShapeUtils,
  ToolType,
} from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { MainRoutes } from '../../../guards/guard.models';
import { FormsStateService } from '../../../services/forms-state/forms-state.service';
import { ParcelTabId } from '../../../services/new-designer-request/new-designer-request.model';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import { ExclusionPurposeService } from '../../construction-portal/services/exclusion-purpose/exclusion-purpose.service';
import { DocsTableEditableComponent } from '../../designer-portal/new-designer-request/docs-table-editable/docs-table-editable.component';
import { ALUNewRequestControlName } from './new-request-form/new-request-form.model';
import { ALUNewRequestFormService } from './new-request-form/new-request-form.service';
import {
  ALUNewRequest,
  ALUNewRequestDto,
  ALUNewRequestFormWorkspaceState,
} from './new-request/new-request.model';

@Component({
  selector: 'app-agricultural-land-use-request',
  templateUrl: './agricultural-land-use-request.component.html',
  standalone: false,
})
export class AgriculturalLandUseRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy
{
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild(DocsTableEditableComponent)
  docsTableEditableComponent: DocsTableEditableComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  override controlName = ALUNewRequestControlName;
  exclusionPurposes: DictionaryField[];
  parcelTabId = ParcelTabId;
  mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE', true),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    true,
    true,
    '25',
    [
      ...this.formsStateService.agriculturalLandUseRequestChosenParcels,
      ...this.formsStateService.agriculturalLandUseRequestExclusionAreas,
    ],
  );
  formValue: ALUNewRequest;
  formNotValidTranslation: string;
  override portalId =
    PortalId.ExcludingLandFromAgriculturalProductionStageFirst;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.ExcludingLandFromAgriculturalProductionStageFirstRequestFormVersion;
  workspaceStateId =
    WorkspaceStateId.ExcludingLandFromAgriculturalProductionStageFirstRequestForm;

  constructor(
    private exclusionPurposesService: ExclusionPurposeService,
    private toastr: ToastrService,
    protected override newRequestHelperService: NewRequestHelperService,
    protected override docSignService: DocSignService,
    protected override router: Router,
    protected override translateService: TranslateService,
    protected override mapSettingsService: MapSettingsService,
    protected override mapStateService: MapStateService,
    public aluNewRequestFormService: ALUNewRequestFormService,
    public formsStateService: FormsStateService,
  ) {
    super(
      undefined,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
    );
  }

  override ngOnInit(): void {
    this.createForm();
    super.subscribeToDocSignTranslations();
    this.subscribeToFormNotValidTranslation();
    this.subscribeToExclusionPurposesList();
    this.fetchApplicantMapSettings();
    this.fetchWrongFileText();
  }

  createForm(): void {
    this.formGroup = this.aluNewRequestFormService.getFormGroup();
  }

  subscribeToFormNotValidTranslation(): void {
    this.translateService
      .get(
        'EXCLUDING_LAND_FROM_AGRICULTURAL_PRODUCTION.VALIDATION.FORM_NOT_VALID_ERROR',
      )
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(
        (formNotValidTranslation) =>
          (this.formNotValidTranslation = formNotValidTranslation),
      );
  }

  subscribeToExclusionPurposesList(): void {
    this.exclusionPurposesService.exclusionPurposes
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((exclusionPurposes) => {
        this.exclusionPurposes = exclusionPurposes;
      });
  }

  override setMapState(mapSettings: MapSettings): void {
    this.mapState = new MapState(
      MapId.NewAgriculturalLandUseRequestParcelsAndExclusionArea,
      this.mapStateService.getViewState(
        MapId.NewAgriculturalLandUseRequestParcelsAndExclusionArea,
        mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.AnyPolygon],
        mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.NewAgriculturalLandUseRequestParcelsAndExclusionArea,
        mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  addUniqueParcelsToList(parcelsToAdd: EgibObject[]): void {
    this.handleMapAction(
      new MapAction(MapObjectTableActionType.AddToExisting, parcelsToAdd),
    );
  }

  areAreaFormControlsValid(): boolean {
    return this.areParcelsChosen() && this.areExclusionAreasChosen();
  }

  areParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  areExclusionAreasChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    if (!this.isRequestValid()) {
      this.toastr.error(this.formNotValidTranslation);
      return;
    }

    this.sendRequest();
  }

  private sendRequest(): void {
    forkJoin([
      this.investorDetailsComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
      this.requestReplyFormComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
      this.proxyDetailsComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
    ])
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap(() => this.getRequestBody()),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/egib/wniosek/wylGrZProdRolnej/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: () => this.handleSendAndValidateFailure(),
      });
  }

  getRequestBody = (): Observable<ALUNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        ALUNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getParcelsMapObjects(),
          this.getExclusionGeomArray(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): ALUNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    parcels: this.getParcelsMapObjects(),
    issueRanges: this.getExclusionGeomArray(),
  });

  handleLoadWorkspaceResponse(state: ALUNewRequestFormWorkspaceState): void {
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    if (state.parcels) {
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
      );
    }
    if (state.issueRanges) {
      const mapObjects = state.issueRanges.map((workRange) =>
        ShapeUtils.getAnyGeometryMapObject(
          workRange,
          MapObjectApiType.ExtentOrPolygon,
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddToExisting, mapObjects),
      );
      this.handleMapAction(
        new MapAction(
          MapViewActionType.ExtentToFitToChange,
          MapExtentUtils.getMapExtentFromMapObjects(mapObjects),
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.SelectRange, mapObjects),
      );
    }
  }

  override handleDocSignSuccess(): void {
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.ExcludingLandFromAgriculturalProductionPortalAgriculturalLandUse}/requests-list`,
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.areParcelsChosen() &&
      this.areExclusionAreasChosen() &&
      this.attachmentsComponent.areDocumentsValid()
    );
  }

  getFormValue(): ALUNewRequest {
    return this.formGroup.getRawValue();
  }

  getParcelsMapObjects(): EgibObject[] {
    return (this.mapState.mapObjectTablesState[0].mapObjects || []).filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getExclusionGeomArray(): string[] {
    const geom = _.get(
      (this.mapState.mapObjectTablesState[0].mapObjects || undefined).filter(
        (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
      )[0],
      'geom',
    );
    return geom ? [geom] : [];
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  checkExclusionPurposeFormControlValidAndFormValidated(): boolean {
    return (
      !this.formGroup.get(this.controlName.ExclusionPurpose).valid &&
      this.submitted
    );
  }

  checkInvestmentCommencementDateFormControlValidAndFormValidated(): boolean {
    return (
      !this.formGroup.get(this.controlName.InvestmentCommencementDate).valid &&
      this.submitted
    );
  }

  checkParcelsAndExclusionAreasValidAndFormValidated(): boolean {
    return (
      (!this.areParcelsChosen() || !this.areExclusionAreasChosen()) &&
      this.submitted
    );
  }
}
